import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { apiBaseUrl } from "../../constants/urls";
import { getToken } from "../../auth/AuthToken";
import { toast } from "react-toastify";

// Building and editing tours. The runner reads its steps through the onboarding
// slice; this one is the authoring side.

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
});

const message = (error, fallback) =>
  error.response?.data?.error ||
  error.response?.data?.message?.join?.(", ") ||
  error.response?.data?.message ||
  fallback;

const initialState = {
  list: [],
  listLoading: false,
  current: null,
  currentLoading: false,
  saving: false,
  analytics: null,
  analyticsLoading: false,
};

export const fetchAdminTours = createAsyncThunk(
  "tours/fetchAll",
  async ({ includeArchived = false } = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/tours`, {
        headers: authHeaders(),
        params: { include_archived: includeArchived },
      });
      return response.data?.tours || [];
    } catch (error) {
      return rejectWithValue(message(error, "Failed to load tours"));
    }
  }
);

export const fetchAdminTour = createAsyncThunk(
  "tours/fetchOne",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/tours/${id}`, {
        headers: authHeaders(),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(message(error, "Failed to load tour"));
    }
  }
);

export const createTour = createAsyncThunk(
  "tours/create",
  async ({ tour }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${apiBaseUrl}/admin/tours`,
        { tour },
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      const msg = message(error, "Failed to save tour");
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

export const updateTour = createAsyncThunk(
  "tours/update",
  async ({ id, tour, quiet = false }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${apiBaseUrl}/admin/tours/${id}`,
        { tour },
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      const msg = message(error, "Failed to save tour");
      if (!quiet) toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

export const duplicateTour = createAsyncThunk(
  "tours/duplicate",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${apiBaseUrl}/admin/tours/${id}/duplicate`,
        {},
        { headers: authHeaders() }
      );
      toast.success("Tour duplicated");
      return response.data;
    } catch (error) {
      const msg = message(error, "Failed to duplicate tour");
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

export const archiveTour = createAsyncThunk(
  "tours/archive",
  async ({ id }, { rejectWithValue }) => {
    try {
      await axios.delete(`${apiBaseUrl}/admin/tours/${id}`, {
        headers: authHeaders(),
      });
      toast.success("Tour archived");
      return { id };
    } catch (error) {
      const msg = message(error, "Failed to archive tour");
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

export const fetchTourAnalytics = createAsyncThunk(
  "tours/analytics",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/tours/${id}/analytics`, {
        headers: authHeaders(),
      });
      return response.data?.analytics || null;
    } catch (error) {
      return rejectWithValue(message(error, "Failed to load analytics"));
    }
  }
);

export const tourSlice = createSlice({
  name: "tours",
  initialState,
  reducers: {
    clearCurrentTour: (state) => {
      state.current = null;
      state.analytics = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminTours.pending, (state) => {
        state.listLoading = true;
      })
      .addCase(fetchAdminTours.fulfilled, (state, action) => {
        state.listLoading = false;
        state.list = action.payload;
      })
      .addCase(fetchAdminTours.rejected, (state) => {
        state.listLoading = false;
      })
      .addCase(fetchAdminTour.pending, (state) => {
        state.currentLoading = true;
      })
      .addCase(fetchAdminTour.fulfilled, (state, action) => {
        state.currentLoading = false;
        state.current = action.payload;
      })
      .addCase(fetchAdminTour.rejected, (state) => {
        state.currentLoading = false;
      })
      .addCase(createTour.pending, (state) => {
        state.saving = true;
      })
      .addCase(createTour.fulfilled, (state, action) => {
        state.saving = false;
        state.current = action.payload;
      })
      .addCase(createTour.rejected, (state) => {
        state.saving = false;
      })
      .addCase(updateTour.pending, (state) => {
        state.saving = true;
      })
      .addCase(updateTour.fulfilled, (state, action) => {
        state.saving = false;
        state.current = action.payload;
      })
      .addCase(updateTour.rejected, (state) => {
        state.saving = false;
      })
      .addCase(archiveTour.fulfilled, (state, action) => {
        state.list = state.list.filter((t) => t.id !== action.payload.id);
      })
      .addCase(fetchTourAnalytics.pending, (state) => {
        state.analyticsLoading = true;
      })
      .addCase(fetchTourAnalytics.fulfilled, (state, action) => {
        state.analyticsLoading = false;
        state.analytics = action.payload;
      })
      .addCase(fetchTourAnalytics.rejected, (state) => {
        state.analyticsLoading = false;
      });
  },
});

export const { clearCurrentTour } = tourSlice.actions;

export default tourSlice.reducer;
